import React, { useContext, useEffect, useState } from 'react'
import Dialog from '../Dialog'
import { Grid } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Photo } from '../../../modules/residence/types/residenceModel'
import SwiperComponent from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { ResponsiveContext } from '../../../utils/context/ResponsiveContext'
import { useTheme } from '@mui/material'
import styles from '../../../styles'
import SvgCircledLeftArrow from '../../../icons/SvgCircledLeftArrow'
import { WHITE } from '../../../theme'
import SvgCircledRightArrow from '../../../icons/SvgCircledRightArrow'
import CloseIcon from '@mui/icons-material/Close'
import { directionEnum } from '../../swiper/enums/directionEnum'

const useStyles = makeStyles<{ isLargeScreen: boolean, swiperWidth: number }>()(
	(theme, { isLargeScreen, swiperWidth }) => ({
		...styles(theme),
		popinContainer: {
			'& .MuiPaper-root': {
				height: isLargeScreen ? 'calc(100% - 64px)' : '100%',
				maxHeight: isLargeScreen ? 800 : 'unset'
			}
		},
		visionneuseContainerStyle: {
			margin: 'auto',
			marginTop: 50,
			height: '100%',
			maxHeight: 'min(100% - 78px, 688px)',
			'& .MuiGrid-item': {
				maxWidth: '100%'
			}
		},
		swiperContainer: {
			height: '100%',
			maxHeight: '75%',

			'& .swiper-slide': {
				textAlign: 'center'
			},
			'& .swiper-pagination-bullet': {
				backgroundColor: WHITE,
				opacity: 0.3
			},
			'& .swiper-pagination-bullet-active': {
				opacity: 1
			}
		},
		swiperButtonsContainer: {
			width: swiperWidth,
			margin: 'auto',
			marginTop: 20,
			justifyContent: 'end'
		},
		swiperButton: {
			marginLeft: 11,
			cursor: 'pointer'
		},
		iconContainer: {
			width: '100%',
			maxWidth: swiperWidth,
			position: 'relative',
			margin: 'auto'
		},
		iconStyle: {
			position: 'absolute',
			top: 15,
			right: 0,
			borderRadius: '100%',
			color: theme.palette.blue.main,
			cursor: 'pointer',
			zIndex: 2,
			fontSize: 25,
			padding: 5,
			background: WHITE,

			'&:hover': {
				background: theme.palette.blue['5']
			}
		}
	})
)

interface AcceptingProps {
	onCloseDialog: () => void,
	isOpen: boolean,
	photos: Omit<Photo, 'visuelMobile'>[],
	swiperWidth: number,
	swiperHeight: number,
	swiperContainerStyle?: string
}

type PopinVisionneusePhotosProps = AcceptingProps

const PopinVisionneusePhotos: React.FC<PopinVisionneusePhotosProps> = (
	{
		onCloseDialog,
		isOpen,
		photos,
		swiperWidth,
		swiperHeight,
		swiperContainerStyle
	}
) => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const { classes, cx } = useStyles({ isLargeScreen, swiperWidth })
	const theme = useTheme()
	const [swipers, setSwipers] = useState<SwiperComponent[]>([])
	const [activeSlide, setActiveSlide] = useState<number>(0)

	const visionneuseContainerClass = cx(
		classes.visionneuseContainerStyle,
		swiperContainerStyle
	)

	const onButtonClicked = (direction: directionEnum) => {
		if (direction === directionEnum.next) {
			swipers.forEach(swiper => {
				swiper.slideNext()
			})
		} else {
			swipers.forEach(swiper => swiper.slidePrev())
		}
	}

	useEffect(
		() => {
			if (!isOpen) {
				setActiveSlide(0)
			}
		}, [isOpen]
	)

	return (
		<Dialog
			onCloseDialog={onCloseDialog}
			open={isOpen}
			fullScreen={!isLargeScreen}
			maxWidth="lg"
			className={classes.popinContainer}
		>
			<>
				<Grid
					container
					item
					xs={12}
					className={classes.iconContainer}
				>
					<CloseIcon
						onClick={onCloseDialog}
						className={classes.iconStyle}
					/>
				</Grid>

				<Grid
					container
					item
					xs={12}
					direction="column"
					className={visionneuseContainerClass}
				>
					<Grid item xs={12}>
						<Swiper
							onSwiper={(s: SwiperComponent) => setSwipers([s])}
							onSlideChange={s => setActiveSlide(s.realIndex)}
							className={classes.swiperContainer}
							pagination={photos.length > 1}
						>
							{
								photos.map(
									(photo, index: number) => (
										<React.Fragment key={`visionneuse-photo-${photo.alt}-${index}`}>
											{
												photo.visuel && (
													<SwiperSlide>
														<img
															src={photo.visuel}
															alt={photo.alt}
															style={{
																width: 0,
																minWidth: '100%',
																maxWidth: '100%',
																height: 0,
																minHeight: '100%',
																maxHeight: '100%',
																objectFit: 'contain'
															}}
														/>
													</SwiperSlide>
												)
											}
										</React.Fragment>
									)
								)
							}
						</Swiper>
						{
							photos.length > 1 && (
								<Grid container item direction="row" xs={12} className={classes.swiperButtonsContainer}>
									<Grid onClick={() => onButtonClicked(directionEnum.previous)} className={classes.swiperButton}>
										{
											activeSlide !== 0 ?
												<SvgCircledLeftArrow color={theme.palette.green.main} secondColor={theme.palette.green.main} thirdColor={WHITE} />
												: <SvgCircledLeftArrow color={theme.palette.blue[10]} secondColor={theme.palette.blue[20]} thirdColor={theme.palette.blue[20]} />
										}
									</Grid>
									<Grid onClick={() => onButtonClicked(directionEnum.next)} className={classes.swiperButton}>
										{activeSlide !== (photos.length - 1) ?
											<SvgCircledRightArrow color={theme.palette.green.main} /> :
											<SvgCircledRightArrow color={theme.palette.blue[10]} secondColor={theme.palette.blue[20]} thirdColor={theme.palette.blue[20]} />
										}
									</Grid>
								</Grid>
							)
						}
					</Grid>
				</Grid>
			</>
		</Dialog>
	)
}

export default PopinVisionneusePhotos